import React, { useEffect, useState, useRef } from 'react';
import CuiButton from '../CuiComponents/Button';
import CuiIcon from '../CuiComponents/Icon';
import FocusTrap from 'focus-trap-react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import axios from 'axios';
import { navigate } from '../../redux/actions/CourseActions';
import { useTranslation } from 'react-i18next';
import useXapi from '../../Hooks/useXapi';
import { actionsToHideModal } from '../../redux/actions/AppHeaderModalActions';
import useFuse from 'use-fuse';
import Loader from '../Block/common/Loading/Loading';
import { pseudolocalize } from '../Block/common/pseudolocalize/pseudolocalize';

import './Search.scss';

let isSearchDataAvailable = false;

const setSearchData = (dataUrlBase, courseId, language) => {
  let searchData = [];
  return dispatch => {
    axios
      .get(`${dataUrlBase}/${courseId}/lang/search_${language.code}.json`)
      .then(resp => {
        searchData = resp.data;
        isSearchDataAvailable = true;
        dispatch({ type: 'UPDATE_SEARCH_DATA', searchData: searchData });
      });
  };
};

const Search = () => {
  const { xApiEvent } = useXapi();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    learningPathJson,
    dataUrlBase,
    courseId,
    language,
    query,
    searchData,
    isPseudo
  } = useSelector(
    state => ({
      learningPathJson: state.CourseState.learningPathJson,
      dataUrlBase: state.CourseState.dataUrlBase,
      courseId: state.CourseState.courseId,
      language: state.CourseState.language,
      query: state.SearchState.query,
      searchData: state.SearchState.searchData,
      isPseudo: state.LanguageState.isPseudo
    }),
    shallowEqual
  );
  const [hasSearched, setHasSearched] = useState(query ? true : false);
  const searchTermEl = useRef(null);

  // see https://fusejs.io/ for details
  const searchOptions = {
    location: 0,
    distance: 100,
    threshold: 0.1,
    maxPatternLength: 32,
    caseSensitive: false,
    tokenSeparator: / +/g,
    findAllMatches: false,
    minMatchCharLength: 1,
    id: null,
    keys: ['text'],
    shouldSort: true,
    sortFn: (a, b) => {
      return a.item.li.split('.')[0] - b.item.li.split('.')[0];
    },
    tokenize: true,
    matchAllTokens: true,
    includeMatches: false,
    includeScore: false,
    verbose: false
  };
  const searchResults = useFuse(searchData, query, searchOptions);
  console.log(searchResults);
  const handleKeyPress = e => {
    return e.key === 'Enter' ? handleSearch(e) : null;
  };

  const handleSearch = e => {
    e.preventDefault();
    if (searchTermEl.current.value.trim().length > 0) {
      dispatch({ type: 'SEARCH_QUERY', query: searchTermEl.current.value });
      setHasSearched(true);
    } else {
      setHasSearched(false);
    }
  };

  const resultClick = (e, item) => {
    e.preventDefault();
    dispatch(navigate(item));
    dispatch({
      type: 'SEARCH_WORDS',
      searchedWords: query.split(' ')
    });
  };

  useEffect(() => {
    if (searchData.length === 0) {
      dispatch(setSearchData(dataUrlBase, courseId, language));
    }
    const bodyEle = document.getElementsByTagName('body')[0];
    // prevent background scrolling while the bookmarks modal is visible
    bodyEle.style.cssText =
      'overflow: hidden; height: auto; padding-right: 15px;';
    searchTermEl.current.focus();
    return function cleanup() {
      bodyEle.style.cssText = '';
    };
  }, [courseId, dataUrlBase, dispatch, searchData, language, query]);

  // based on search results, get the LIs for mods, blocks and chunks
  // Remove dupes with Set
  // convert back to array
  // Keeps the order in which the results are ranked (most relevant?)
  const moduleLIs = [
    ...new Set(
      searchResults.map(r => {
        let splitLi = r.li.split('.');
        return `${splitLi[0]}`;
      })
    )
  ];
  const blockLIs = [
    ...new Set(
      searchResults.map(r => {
        let splitLi = r.li.split('.');
        return `${splitLi[0]}.${splitLi[1]}`;
      })
    )
  ];
  const chunkLIs = [
    ...new Set(
      searchResults.filter(r => r.li.split('.').length === 3).map(r => r.li)
    )
  ];
  return (
    <FocusTrap>
      <div className="search-outer">
        <div className="search-header-stuck">
          <div>
            <div className="icon-circle-wrap">
              <CuiIcon icon="search" size="medium" />
            </div>
            <h1>{t('Search.search')}</h1>
          </div>
          <CuiButton
            id="close-btn"
            icon="close"
            onClick={() => {
              xApiEvent('Search Modal Closed', window.location.href);
              dispatch(actionsToHideModal('HIDE_SEARCH'));
            }}
          />
        </div>
        <div className="search-inner">
          <div className="search">
            <div className="search-input-wrap">
              <span className="icon-search" />
              <input
                ref={searchTermEl}
                type="text"
                className="search-input"
                placeholder={t('Search.placeholder')}
                defaultValue={query}
                onKeyPress={e => handleKeyPress(e)}
                onKeyUp={e => {
                  if (e.keyCode === 27) {
                    dispatch(actionsToHideModal('HIDE_SEARCH'));
                  }
                }}
              />
              {query !== undefined && (
                <span
                  onClick={() => {
                    searchTermEl.current.value = '';
                    dispatch({ type: 'SEARCH_QUERY', query: undefined });
                    dispatch({ type: 'SEARCH_WORDS', searchedWords: [] });
                    setHasSearched(false);
                  }}
                  className="clear-search icon-close"
                ></span>
              )}
              <button
                className="btn btn--white-ghost"
                onClick={e => handleSearch(e)}
              >
                {t('Search.search')}
              </button>
            </div>
          </div>
        </div>

        <div className="results-wrap">
          {hasSearched && !isSearchDataAvailable && <Loader context="Search" />}
          {hasSearched && isSearchDataAvailable && (
            <div className={`results-count-wrap`}>
              <div
                className={`results-count ${
                  searchResults.length ? 'has-results' : 'no-results'
                }`}
              >
                {searchResults.length ? `${chunkLIs.length} ` : '0 '}
                {t('Search.resultsFor')} "{query}"
              </div>
            </div>
          )}
          {hasSearched && searchResults.length > 0 && (
            <ul className={`modules-container`}>
              {moduleLIs.map(modli => {
                let mod = learningPathJson.hasPart.find(m => m.li === modli);
                return (
                  <li key={mod.li} className="module-item">
                    <div className={`module-title`}>
                      {mod.li} -{' '}
                      {isPseudo ? pseudolocalize(mod.name) : mod.name}
                    </div>
                    <ul className="blocks-container">
                      {blockLIs.map(blockLI => {
                        let block = mod.hasPart.find(b => b.li === blockLI);
                        if (!block) return '';
                        return (
                          <li key={block.li} className="block-item">
                            <div className="block-title">
                              <a href=" " onClick={e => resultClick(e, block)}>
                                {/* strip out the markdown coming thru in block.name */}
                                {block.li} -{' '}
                                {isPseudo
                                  ? pseudolocalize(block.name.replace('# ', ''))
                                  : block.name.replace('# ', '')}
                              </a>
                            </div>
                            <ul className="chunks-container">
                              {chunkLIs.map(chunkLI => {
                                let chunk = block.chunks.find(
                                  c => c.li === chunkLI
                                );
                                if (!chunk) return '';
                                return (
                                  <li key={chunk.li} className="chunk-item">
                                    <a
                                      href=" "
                                      onClick={e => resultClick(e, chunk)}
                                    >
                                      {chunk.li} -{' '}
                                      {isPseudo
                                        ? pseudolocalize(chunk.name)
                                        : chunk.name}
                                    </a>
                                  </li>
                                );
                              })}
                            </ul>
                          </li>
                        );
                      })}
                    </ul>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </FocusTrap>
  );
};
export default Search;
